/**
 * DomainTriangulationView (DC-1492)
 *
 * Per-domain triangulated FAIR Assessment screen at /triangulation/:domainId.
 * Renders four PillarCards (Findable / Accessible / Interoperable / Reusable)
 * in canonical pillar order. Each card composes the three leg readings,
 * coherence, and named pattern — Anuj locked the card-per-pillar layout on
 * 2026-05-14.
 *
 * Editorial redesign (2026-05-26): first Compass surface ported to the
 * cream/teal "Point-of-View" design language (scoped under `.tri-view`). The
 * screen now leads with a domain-level synthesis band — the executive read the
 * user guide keeps modelling — computed honestly from the real leg states.
 *
 * Data: GET /api/triangulation/domain/:domainId → DomainTriangulation
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FileText, AlertCircle, Users, MessageSquare, Database } from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import { PillarCard } from '../../components/triangulation/PillarCard';
import { PersonaTargetsPanel } from '../../components/triangulation/PersonaTargetsPanel';
import { legBreached, legSufficient } from '../../components/triangulation/legUtils';
import { RemediationPlaybookPanel } from '../../components/triangulation/RemediationPlaybookPanel';
import { CrossDatasetRollupSection } from './CrossDatasetRollupSection';
import type {
  DocumentedLegContribution,
  PerceivedLegContribution,
  TechnicalLegContribution,
  DomainTriangulation,
} from '../../types/triangulation';
import { FAIR_PILLAR_NAMES } from '../../types/triangulation';
import '../../styles/triangulation.css';

// DC-1658: the domain trajectory chart is route-split — ApexCharts is heavy and
// belongs behind a lazy boundary, not in the entry chunk (UX spec §7).
const DomainTrajectoryChart = React.lazy(
  () => import('../../components/trajectory/DomainTrajectoryChart'),
);

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: DomainTriangulation }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

export const DomainTriangulationView: React.FC = () => {
  const { domainId } = useParams<{ domainId: string }>();
  // Destructure stable references — see TriangulationDashboard for the
  // infinite-render rationale.
  const { authFetch } = useApiClient();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // DC-1602: per-document attribution for the Documented leg. Loaded on a
  // separate request so a 404/500 on the contributions endpoint doesn't
  // block the main triangulation render.
  const [contributions, setContributions] = useState<DocumentedLegContribution[] | null>(null);
  // DC-1628: per-leg sources for the Perceived (interviews + mapped speakers)
  // and Technical (datasets evaluated/pending) provenance panels. Loaded on
  // separate fire-and-forget requests so a failure never blocks the main view.
  const [perceived, setPerceived] = useState<PerceivedLegContribution[] | null>(null);
  const [technical, setTechnical] = useState<TechnicalLegContribution[] | null>(null);

  // DC-1593: stale-response race. `load` is keyed on `[authFetch, domainId]`.
  // If `domainId` changes (route navigation, `<Link>` swap, React Strict
  // Mode double-fire) before the in-flight fetch resolves, the old fetch's
  // setState lands AFTER the new fetch started — overwriting the new
  // domain's view with stale data. The latestDomainIdRef captures the most
  // recent request; the bail-out checks happen after every await.
  const latestDomainIdRef = useRef<string | undefined>(domainId);
  useEffect(() => {
    latestDomainIdRef.current = domainId;
  }, [domainId]);

  const load = useCallback(async (): Promise<void> => {
    if (!domainId) {
      setState({ kind: 'error', message: 'Missing domain ID in URL.' });
      return;
    }
    const requestedDomainId = domainId;
    setState({ kind: 'loading' });
    setContributions(null);
    setPerceived(null);
    setTechnical(null);
    try {
      // Use authFetch so we can distinguish 404 from other errors without
      // relying on the error-message string. roleBreakdown=true (DC-1612)
      // opts into the role-partitioned Perceived reading for the gap strip.
      const res = await authFetch(
        `/api/triangulation/domain/${requestedDomainId}?roleBreakdown=true`,
      );
      if (requestedDomainId !== latestDomainIdRef.current) return; // stale
      if (res.status === 404) {
        setState({ kind: 'not_found' });
        return;
      }
      if (!res.ok) {
        const errorText = await res.text();
        if (requestedDomainId !== latestDomainIdRef.current) return; // stale
        let message = errorText || `HTTP ${res.status}`;
        try {
          const json = JSON.parse(errorText) as { error?: { message?: string } };
          if (json.error?.message) message = json.error.message;
        } catch {
          /* keep raw text */
        }
        setState({ kind: 'error', message });
        return;
      }
      const data = (await res.json()) as DomainTriangulation;
      if (requestedDomainId !== latestDomainIdRef.current) return; // stale
      setState({ kind: 'ready', data });
      // DC-1602: load documented-leg contributions only after the main
      // payload succeeds. Failing to load contributions is silent — the
      // main view still renders without the Documented Sources panel.
      // Fire-and-forget; the inner function carries its own stale-bail.
      void loadContributions(authFetch, requestedDomainId, latestDomainIdRef, setContributions);
      // DC-1628: Perceived + Technical leg sources, same fire-and-forget +
      // stale-bail discipline. Each panel is supplementary; failures are silent.
      void loadContributionList<PerceivedLegContribution>(
        authFetch,
        `/api/triangulation/domain/${requestedDomainId}/perceived-contributions`,
        requestedDomainId,
        latestDomainIdRef,
        setPerceived,
      );
      void loadContributionList<TechnicalLegContribution>(
        authFetch,
        `/api/triangulation/domain/${requestedDomainId}/technical-contributions`,
        requestedDomainId,
        latestDomainIdRef,
        setTechnical,
      );
    } catch (err) {
      if (requestedDomainId !== latestDomainIdRef.current) return; // stale
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to load triangulation',
      });
    }
  }, [authFetch, domainId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="tri-view">
      <div className="tri-inner">
        {/* DC-1646: the domain list is the Readiness Data lens */}
        <Link to="/readiness" className="tri-back" data-testid="back-to-list">
          <ArrowLeft size={14} aria-hidden="true" />
          <span>All domains</span>
        </Link>

        {state.kind === 'loading' && (
          <div className="tri-status" role="status" aria-live="polite">
            Loading triangulation…
          </div>
        )}

        {state.kind === 'not_found' && (
          <div className="tri-alert" role="alert" data-testid="not-found">
            <AlertCircle size={20} aria-hidden="true" />
            <div>
              <p className="tri-alert-title">Domain not found</p>
              <p className="tri-alert-detail">
                This domain either doesn’t exist or isn’t accessible to your organization.
              </p>
            </div>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="tri-alert" role="alert">
            <AlertCircle size={20} aria-hidden="true" />
            <div>
              <p className="tri-alert-title">Couldn’t load triangulation</p>
              <p className="tri-alert-detail">{state.message}</p>
              <button type="button" onClick={load} className="tri-retry">
                Retry
              </button>
            </div>
          </div>
        )}

        {state.kind === 'ready' && (
          <>
            <header>
              <div className="tri-eyebrow">Triangulated FAIR Assessment</div>
              <h1 className="tri-domain">{state.data.domain.name}</h1>
              {state.data.domain.description && (
                <p className="tri-domain-desc">{state.data.domain.description}</p>
              )}
              <div className="tri-stats" data-testid="domain-meta">
                <Stat value={state.data.metadata.dataset_count} label={pluralize('Dataset', state.data.metadata.dataset_count)} />
                <Stat value={state.data.metadata.interview_count} label="Interviews" />
                <Stat value={state.data.metadata.document_count} label="Documents" />
                {state.data.metadata.last_updated && (
                  <LastUpdated iso={state.data.metadata.last_updated} />
                )}
              </div>
            </header>

            <SynthesisBand data={state.data} />

            {/* DC-1702: cross-dataset rollup — collapsed by default, lazy-
                fetched on first expand (no new fetch on the demo path). */}
            {domainId && <CrossDatasetRollupSection domainId={domainId} />}

            <div className="tri-section-label">The four pillars</div>
            <div className="tri-grid" data-testid="pillar-grid">
              {/* Render in canonical pillar order even if API ever returns out of order. */}
              {FAIR_PILLAR_NAMES.map((pillarName) => {
                const cell = state.data.pillars.find((c) => c.pillar === pillarName);
                if (!cell) return null;
                return (
                  <PillarCard
                    key={pillarName}
                    cell={cell}
                    declaredByName={state.data.domain.documented_state_set_by_name}
                    declaredAt={state.data.domain.documented_state_set_at}
                    methodologyVersion={state.data.metadata.thresholds.qualifier_methodology_version}
                  />
                );
              })}
            </div>

            {/* C2 (migration 140): the Perceived coverage denominator — the
                domain's curated persona target population. Status changes
                move coverage, so the view re-fetches on change. */}
            {domainId && (
              <PersonaTargetsPanel domainId={domainId} onTargetsChanged={() => void load()} />
            )}

            {/* DC-1673: prescriptive remediation playbook for the fired patterns */}
            {domainId && <RemediationPlaybookPanel domainId={domainId} />}

            {/* DC-1658: domain readiness trajectory over time */}
            {domainId && (
              <React.Suspense fallback={<div className="dtj-skeleton" aria-hidden="true" />}>
                <DomainTrajectoryChart domainId={domainId} />
              </React.Suspense>
            )}

            {contributions && contributions.length > 0 && (
              <DocumentedSources contributions={contributions} />
            )}

            {perceived && perceived.length > 0 && (
              <PerceivedSources contributions={perceived} />
            )}

            {technical && technical.length > 0 && (
              <TechnicalSources contributions={technical} />
            )}

            <div className="tri-byline">
              <span>DATA Compass · Triangulated FAIR Assessment</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Domain synthesis band — the executive roll-up the four cards can't give on
// their own. Computed ENTIRELY from the real cell states; never invents a
// pattern. DC-1492 redesign 2026-05-26.
// ============================================================================

// v2.5 SS-03: legBreached + sufficiency checks moved to the SHARED legUtils
// (the twin copies here and in PillarCard previously drifted).

const LEG_PHRASE = {
  technical: 'automated FAIR scoring (Technical)',
  perceived: 'stakeholder interviews (Perceived)',
  documented: 'governance documents (Governed)',
} as const;

const SynthesisBand: React.FC<{ data: DomainTriangulation }> = ({ data }) => {
  const pillars = data.pillars;
  const n = pillars.length;

  const presentIn = (leg: 'technical' | 'perceived' | 'documented'): number =>
    pillars.filter((p) => p[leg].state === 'present_and_scored').length;
  const tIn = presentIn('technical');
  const pIn = presentIn('perceived');
  const dIn = presentIn('documented');

  // v2.5 SS-03: 'provisional' is its own bucket — a full-basis pattern with
  // preliminary contributing coverage is no longer counted as confirmed.
  let confirmed = 0;
  let provisional = 0;
  let suspected = 0;
  let suspended = 0;
  let mixed = 0;
  for (const p of pillars) {
    if (p.pattern !== null) {
      if (p.pattern_qualifier === 'suspected') suspected += 1;
      else if (p.pattern_qualifier === 'provisional') provisional += 1;
      else confirmed += 1;
    } else if (legBreached(p, 'technical') || legBreached(p, 'perceived')) {
      suspended += 1;
    } else {
      mixed += 1;
    }
  }

  const anyDivergence = pillars.some((p) => p.coherence !== null && p.coherence < 0.8);

  // The cleanest, most common engagement shape: exactly one hard-floor leg is
  // entirely missing while the other two are fully in. Lead with that.
  const legsIn: Array<'technical' | 'perceived' | 'documented'> = [];
  if (tIn === n) legsIn.push('technical');
  if (pIn === n) legsIn.push('perceived');
  if (dIn === n) legsIn.push('documented');
  const technicalMissing = tIn === 0;
  const perceivedMissing = pIn === 0;

  let headline: React.ReactNode;
  let cta: string | null = null;

  if (legsIn.length === 2 && (technicalMissing || perceivedMissing)) {
    const missingLeg = technicalMissing ? 'technical' : 'perceived';
    headline = (
      <>
        Two of three signals are in — <b>{LEG_PHRASE[legsIn[0]]}</b> and{' '}
        <b>{LEG_PHRASE[legsIn[1]]}</b> — but <b>{LEG_PHRASE[missingLeg]} hasn’t been gathered</b>,
        so no pattern is confirmed yet.
      </>
    );
    cta = technicalMissing ? 'Run FAIR scoring' : 'Gather stakeholder interviews';
  } else if (confirmed === n) {
    // G0 §6: the three-signals claim only when every basis is FULL. With a
    // declared absence, the honest headline is the two available legs.
    const allFullBasis = pillars.every((p) => p.evidence_basis === 'full');
    headline = allFullBasis ? (
      <>
        <b>All {n} pillars confirmed</b> across three independent signals — Technical, Perceived,
        and Governed agree at sufficient coverage.
      </>
    ) : (
      <>
        <b>All {n} pillars confirmed by the two available legs</b> — Technical and Perceived agree
        at sufficient coverage; the Governed signal was deliberately declared absent during
        assessment setup.
      </>
    );
  } else {
    const parts = [
      `${confirmed} of ${n} confirmed`,
      provisional ? `${provisional} provisional (coverage preliminary)` : null,
      suspected ? `${suspected} suspected` : null,
      suspended ? `${suspended} awaiting evidence` : null,
      mixed ? `${mixed} under review` : null,
    ].filter(Boolean);
    headline = (
      <>
        <b>{parts.join(' · ')}.</b> The read is uneven across pillars — open each card for its
        diagnosis.
      </>
    );
  }

  const subParts: string[] = [];
  if (technicalMissing) {
    subParts.push(
      `The Technical leg has ${data.metadata.dataset_count} ${pluralize('dataset', data.metadata.dataset_count).toLowerCase()} tagged — below the 5-dataset floor.`,
    );
  }
  // v2.5 SS-03: "sufficient coverage" now means SUFFICIENCY, not mere
  // presence — a leg at preliminary coverage no longer earns this line;
  // it gets the honest preliminary disclosure instead.
  const pSufficient = pillars.filter((p) => legSufficient(p, 'perceived')).length;
  const tPreliminaryAny = pillars.some(
    (p) => p.technical.state === 'present_and_scored' && p.technical.coverage.sufficiency === 'preliminary',
  );
  const pPreliminaryAny = pillars.some(
    (p) => p.perceived.state === 'present_and_scored' && p.perceived.coverage.sufficiency === 'preliminary',
  );
  if (pSufficient === n) subParts.push('Perceived is at sufficient coverage across all pillars.');
  else if (pPreliminaryAny) subParts.push('Perceived coverage is preliminary on at least one pillar.');
  if (tPreliminaryAny) subParts.push('Technical coverage is preliminary on at least one pillar.');
  if (dIn === n) subParts.push('Governed evidence is in for every pillar.');
  if (anyDivergence) subParts.push('At least one pillar already shows its legs diverging.');

  return (
    <div className="tri-synthesis" data-testid="synthesis-band">
      <div>
        <div className="tri-synth-eyebrow">The read across all {n} pillars</div>
        <p className="tri-synth-headline">{headline}</p>
        {subParts.length > 0 && <p className="tri-synth-sub">{subParts.join(' ')}</p>}
      </div>
      {cta && (
        <span className="tri-cta" role="note" data-testid="synthesis-cta">
          {cta}
          <ArrowRight size={15} aria-hidden="true" />
        </span>
      )}
    </div>
  );
};

// ============================================================================
// Documented sources — DC-1602
// ============================================================================

/**
 * Fetch contributing documents for the Documented leg, with the same
 * stale-bail discipline as the main load. Silent on failure — the
 * Documented Sources panel is supplementary and the rest of the view
 * renders correctly without it.
 */
async function loadContributions(
  authFetch: (url: string, init?: RequestInit) => Promise<Response>,
  requestedDomainId: string,
  latestDomainIdRef: React.MutableRefObject<string | undefined>,
  setContributions: React.Dispatch<React.SetStateAction<DocumentedLegContribution[] | null>>,
): Promise<void> {
  try {
    const res = await authFetch(
      `/api/triangulation/domain/${requestedDomainId}/documented-contributions`,
    );
    if (requestedDomainId !== latestDomainIdRef.current) return; // stale
    if (!res.ok) return; // silent failure
    const data = (await res.json()) as DocumentedLegContribution[];
    if (requestedDomainId !== latestDomainIdRef.current) return; // stale
    setContributions(data);
  } catch {
    // Silent — contributions are supplementary, not load-bearing.
  }
}

/**
 * Generic supplementary-list loader (DC-1628) with the same stale-bail +
 * silent-failure discipline as `loadContributions`. Used for the Perceived
 * and Technical sources panels.
 */
async function loadContributionList<T>(
  authFetch: (url: string, init?: RequestInit) => Promise<Response>,
  url: string,
  requestedDomainId: string,
  latestDomainIdRef: React.MutableRefObject<string | undefined>,
  setter: React.Dispatch<React.SetStateAction<T[] | null>>,
): Promise<void> {
  try {
    const res = await authFetch(url);
    if (requestedDomainId !== latestDomainIdRef.current) return; // stale
    if (!res.ok) return; // silent failure
    const data = (await res.json()) as T[];
    if (requestedDomainId !== latestDomainIdRef.current) return; // stale
    setter(data);
  } catch {
    // Silent — supplementary, not load-bearing.
  }
}

/**
 * Per-document attribution for the Documented leg. Group-source documents
 * (category === 'group_transcript') render with a "Group Source" chip and
 * session-level attribution ("Manufacturing Roundtable, May 25, 2026")
 * instead of any individual persona attribution. Other categories render
 * with just the document name.
 */
const DocumentedSources: React.FC<{ contributions: DocumentedLegContribution[] }> = ({
  contributions,
}) => (
  <section className="tri-sources" data-testid="documented-sources">
    <h2>Governed sources</h2>
    <p className="tri-sources-caption">
      {contributions.length} governance {pluralize('document', contributions.length).toLowerCase()}{' '}
      scored against 8 regulatory frameworks — GxP, ALCOA+, 21 CFR Part 11, GDPR, FDA AI/ML, EU AI
      Act, Agent Governance, SR 11-7.
    </p>
    <ul className="tri-src-list">
      {contributions.map((c) => (
        <li key={c.document_id} className="tri-src-item" data-testid="documented-source-item">
          {c.category === 'group_transcript' ? (
            <Users size={15} aria-hidden="true" />
          ) : (
            <FileText size={15} aria-hidden="true" />
          )}
          <span className="tri-src-name">{formatSourceLabel(c)}</span>
          {c.category === 'group_transcript' && (
            <span className="tri-src-chip" data-testid="group-source-chip">
              Group Source
            </span>
          )}
        </li>
      ))}
    </ul>
  </section>
);

function formatSourceLabel(c: DocumentedLegContribution): string {
  if (c.category === 'group_transcript') {
    // Session-level attribution. No individual persona.
    const dateLabel = c.analyzed_at ? formatSessionDate(c.analyzed_at) : 'undated';
    return `${c.document_name}, ${dateLabel}`;
  }
  return c.document_name;
}

function formatSessionDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ============================================================================
// Perceived sources — DC-1628
// ============================================================================

/**
 * Contributing sources for the Perceived leg: 1:1 interviews (labelled by
 * interviewee role) and mapped group-session speakers (labelled by speaker
 * name with a "Group session" chip + the parent session). Mirrors the
 * Documented sources panel so the provenance section reads symmetrically.
 */
const PerceivedSources: React.FC<{ contributions: PerceivedLegContribution[] }> = ({
  contributions,
}) => {
  const interviews = contributions.filter((c) => c.source === 'interview').length;
  const speakers = contributions.filter((c) => c.source === 'group_speaker').length;
  const parts: string[] = [];
  if (interviews > 0) {
    parts.push(`${interviews} stakeholder ${pluralize('interview', interviews).toLowerCase()}`);
  }
  if (speakers > 0) {
    parts.push(`${speakers} mapped group ${pluralize('speaker', speakers).toLowerCase()}`);
  }
  return (
    <section className="tri-sources" data-testid="perceived-sources">
      <h2>Perceived sources</h2>
      <p className="tri-sources-caption">
        {parts.join(' · ')} feeding the persona-weighted Perceived leg.
      </p>
      <ul className="tri-src-list">
        {contributions.map((c) => (
          <li key={`${c.source}:${c.id}`} className="tri-src-item" data-testid="perceived-source-item">
            {c.source === 'group_speaker' ? (
              <Users size={15} aria-hidden="true" />
            ) : (
              <MessageSquare size={15} aria-hidden="true" />
            )}
            <span className="tri-src-name">
              {c.label}
              {c.session_name ? ` — ${c.session_name}` : ''}
            </span>
            {c.source === 'group_speaker' && (
              <span className="tri-src-chip" data-testid="group-speaker-chip">
                Group session
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

// ============================================================================
// Technical sources — DC-1628
// ============================================================================

/**
 * Datasets tagged to the domain for the Technical leg. Lists evaluated AND
 * not-yet-evaluated datasets so that when the leg reads Insufficient the user
 * can see exactly which datasets were assessed and which are still pending —
 * directly answering "which datasets HAVE been evaluated."
 */
const TechnicalSources: React.FC<{ contributions: TechnicalLegContribution[] }> = ({
  contributions,
}) => {
  const evaluated = contributions.filter((c) => c.evaluated).length;
  const total = contributions.length;
  const belowFloor = evaluated < 5;
  return (
    <section className="tri-sources" data-testid="technical-sources">
      <h2>Technical sources</h2>
      <p className="tri-sources-caption">
        {evaluated} of {total} {pluralize('dataset', total).toLowerCase()} evaluated by the FAIR
        engine{belowFloor ? ' — below the 5-dataset sufficiency floor' : ''}.
      </p>
      <ul className="tri-src-list">
        {contributions.map((c) => (
          <li key={c.journey_id} className="tri-src-item" data-testid="technical-source-item">
            <Database size={15} aria-hidden="true" />
            <span className="tri-src-name">{c.name}</span>
            {c.evaluated ? (
              <span className="tri-src-chip tri-src-chip-ok" data-testid="dataset-evaluated">
                Evaluated{c.overall_score !== null ? ` · ${c.overall_score.toFixed(1)}/5` : ''}
              </span>
            ) : (
              <span className="tri-src-chip tri-src-chip-pending" data-testid="dataset-pending">
                Not yet evaluated
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`;
}

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="tri-stat">
    <span className="tri-stat-num">{value}</span>
    <span className="tri-stat-lbl">{label}</span>
  </div>
);

const LastUpdated: React.FC<{ iso: string }> = ({ iso }) => {
  const parsed = new Date(iso);
  const valid = !Number.isNaN(parsed.getTime());
  const text = valid ? parsed.toLocaleString() : iso;
  return (
    <div
      className="tri-stat tri-stat-updated"
      title={`Most recent contributing leg data: ${text}`}
      data-testid="last-updated"
    >
      <span className="tri-stat-num">Updated {text}</span>
    </div>
  );
};
